import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Boxes, FolderTree, Plus, Sparkles } from "lucide-react";
import {
  useInventory,
  KIND_LABELS,
  type InventoryItem,
  type InventoryKind,
} from "@/lib/inventory";
import { formatMoney } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/v2/StatCard";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { InventoryItemPanel } from "@/components/v2/InventoryItemPanel";
import { NewItemDrawer } from "./Inventory";

/**
 * Category drill-in for the Inventory section. Mirrors the ServiceDetail
 * shell (breadcrumb → header → body) and renders the per-category items
 * table that used to be the entire `/inventory` page.
 *
 * URL: `/inventory/:category` — `:category` is the URL-encoded category
 * name (free-text, not a slug). Special case: `Uncategorized` shows items
 * with `category = null`.
 */

const UNCATEGORIZED = "Uncategorized";

export function InventoryCategory() {
  const { category: rawCategory } = useParams<{ category: string }>();
  const category = rawCategory ? decodeURIComponent(rawCategory) : null;
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const overview = useInventory();
  const all = (overview.data?.items ?? []) as InventoryItem[];

  const items = useMemo(() => {
    if (!category) return [];
    if (category === UNCATEGORIZED) {
      return all.filter((i) => !i.category?.trim());
    }
    return all.filter((i) => i.category?.trim() === category);
  }, [all, category]);

  if (!category) return <Navigate to="/inventory" replace />;

  // Legacy bucket for items with no category — new items always belong to a
  // real category, so item creation is hidden here.
  const isUncategorized = category === UNCATEGORIZED;

  const kindMix = new Set(items.map((i) => i.kind));
  const kindLabel: "Plans" | "Physical" | "Mixed" =
    kindMix.size > 1 ? "Mixed" : kindMix.has("plan") ? "Plans" : "Physical";
  const inStock = items.filter((i) => i.kind === "physical" && i.qtyOnHand > 0).length;
  const totalValue = items.reduce(
    (s, i) =>
      s + Number(i.unitPrice ?? 0) * (i.kind === "physical" ? i.qtyOnHand : 1),
    0,
  );

  const columns: Column<InventoryItem>[] = [
    {
      key: "name",
      header: "Item Name",
      searchAccessor: (i) => `${i.name} ${i.sku ?? ""}`,
      cell: (i) => (
        <div className="min-w-0">
          <p className="font-medium text-sm leading-tight truncate">{i.name}</p>
          {i.sku && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">SKU {i.sku}</p>
          )}
        </div>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (i) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
            i.kind === "plan"
              ? "bg-primary/10 text-primary"
              : "bg-muted text-foreground"
          }`}
        >
          {i.kind === "plan" ? (
            <Sparkles className="h-3 w-3" />
          ) : (
            <Boxes className="h-3 w-3" />
          )}
          {KIND_LABELS[i.kind as InventoryKind] ?? i.kind}
        </span>
      ),
    },
    {
      key: "qty",
      header: "Stock",
      align: "right",
      // Plans are "Unlimited" — sort them past everything else with Infinity.
      sortAccessor: (i) => (i.kind === "plan" ? Infinity : i.qtyOnHand),
      cell: (i) =>
        i.kind === "plan" ? (
          <span className="text-xs text-muted-foreground italic">Unlimited</span>
        ) : (
          <span className="text-sm tabular-nums">
            {i.qtyOnHand} {i.unit ?? ""}
          </span>
        ),
    },
    {
      key: "price",
      header: "Price",
      align: "right",
      sortAccessor: (i) => (i.unitPrice != null ? Number(i.unitPrice) : null),
      cell: (i) =>
        i.unitPrice != null ? (
          <span className="text-sm font-semibold tabular-nums">
            {formatMoney(i.currency, i.unitPrice)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      // Ordinal so the states sort meaningfully:
      //   active(0) → draft(2) → cancelled(3)
      sortAccessor: (i) => {
        if (i.kind === "plan") return i.active ? 0 : 2;
        if (i.qtyOnHand === 0) return 3;
        return 0;
      },
      cell: (i) =>
        i.kind === "plan" ? (
          <StatusBadge label={i.active ? "active" : "draft"} />
        ) : i.qtyOnHand === 0 ? (
          <StatusBadge label="cancelled" />
        ) : (
          <StatusBadge label="active" />
        ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary-light text-primary inline-flex items-center justify-center shrink-0">
            <FolderTree className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight truncate">
                {category}
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                  kindLabel === "Plans"
                    ? "bg-primary/10 text-primary"
                    : kindLabel === "Physical"
                      ? "bg-muted text-foreground"
                      : "bg-status-bg-pending text-status-pending"
                }`}
              >
                {kindLabel === "Plans" ? (
                  <Sparkles className="h-3 w-3" />
                ) : (
                  <Boxes className="h-3 w-3" />
                )}
                {kindLabel}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 tabular-nums">
              {items.length} item{items.length === 1 ? "" : "s"} in this category
            </p>
          </div>
        </div>
        {!isUncategorized && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Item
          </Button>
        )}
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Items" value={items.length} hint="In this category" />
        <StatCard label="In stock" value={inStock} hint="Physical only" />
        <StatCard
          label="Total value"
          value={formatMoney(
            items.find((i) => !!i.currency)?.currency ?? "PHP",
            totalValue,
          )}
          hint="Stock × unit price"
        />
      </div>

      {overview.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {overview.isError && (
        <p className="text-sm text-destructive">{(overview.error as Error).message}</p>
      )}

      {overview.data && (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(i) => i.id}
          onRowClick={(i) => setOpenItemId(i.id)}
          sortable
          resizable
          hideable
          searchable
          searchPlaceholder="Search items by name or SKU…"
          pagination="client"
          pageSize={30}
          exportCsv={{
            filename: `inventory-${category.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`,
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                icon={Boxes}
                title="No items yet"
                description={
                  isUncategorized
                    ? "Legacy items without a category show up here."
                    : "This category has no items yet — add one to get started."
                }
                action={
                  isUncategorized ? undefined : (
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Item
                    </Button>
                  )
                }
              />
            </div>
          }
        />
      )}

      <InventoryItemPanel
        itemId={openItemId}
        onClose={() => setOpenItemId(null)}
      />

      {!isUncategorized && (
        <NewItemDrawer
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          category={category}
        />
      )}
    </div>
  );
}
